import React, { useEffect, useMemo, useState } from "react"
import { Filter, FilterX } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Button, Col, Container, Nav, NavItem, NavLink, Row } from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"
import Spinners from "components/Shared/Spinners"
import Filters from "components/Shared/Filters"
import MarkAttendanceModal from "./components/MarkAttendanceModal"
import TableComponent from "./components/TableComponent"

import { showAlert } from "store/reducers/alertReducer"
import {
  setPayload,
  resetPayload,
  toggleFilterVisibility,
  setActiveTab,
} from "store/reducers/practicalTrainingReducer"
import {
  deletePracticalTraining,
  getAllPracticalTrainings,
} from "store/actions/practicalTrainingAction"
import { usePermissions } from "hooks"
import { API_URL, formatDateForAPI } from "components/Common/Utils"
import { useNavigate } from "react-router-dom"
import CandidateTabs from "./components/CandidateTabs"
import axios from "axios"

const navItems = [
  { name: "Training Rooms", id: 0 },
  { name: "Enrolled", id: 1 },
  { name: "Present", id: 2 },
  { name: "Absent", id: 5 },
  { name: "Passed", id: 3 },
  { name: "Failed", id: 4 },
  { name: "Not Invited", id: 6 },
]

const PracticalTraining = () => {
  const {
    loading,
    isLoading,
    practicalTrainings,
    totalPages,
    totalCount,
    currentPage,
    payload,
    isShowFilter,
    activeTab,
    participantsListPayload,
  } = useSelector(state => state.PracticalTraining)
  const navigate = useNavigate()

  const { permissions, showAll } = usePermissions("practical_training")

  const { t } = useTranslation()
  document.title = t("Practical Training | Yamama")

  const dispatch = useDispatch()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [modal, setModal] = useState(false)
  const [modalType, setModalType] = useState(null)
  const toggle = () => setModal(!modal)
  const [selectedUuid, setSelectedUuid] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [trigger, setTrigger] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const filters = useMemo(() => {
    return {
      keyword: {
        type: "text",
        placeholder: t("Search by name"),
        label: t("Search By"),
        colSize: 4,
      },
      date_range: { type: "dateRange", label: t("Date Range"), colSize: 4 },
      order_by: {
        type: "select",
        placeholder: t("Select Order"),
        label: t("Select Order"),
        options: [
          { value: 1, label: t("Ascending") },
          { value: -1, label: t("Descending") },
        ],
        colSize: 4,
      },
    }
  }, [t])

  useEffect(() => {
    getAllData()
  }, [trigger])

  const getAllData = () => {
    let data = { ...payload }
    if (data?.filters?.date_range?.length > 1) {
      data = {
        ...data,
        filters: {
          ...data.filters,
          date_range: {
            start_date: formatDateForAPI(data.filters.date_range[0]),
            end_date: formatDateForAPI(data.filters.date_range[1]),
          },
        },
      }
    } else if (data?.filters?.date_range?.length === 1) {
      data = {
        ...data,
        filters: {
          ...data.filters,
          date_range: {
            start_date: formatDateForAPI(data.filters.date_range[0]),
            end_date: formatDateForAPI(data.filters.date_range[0]),
          },
        },
      }
    } else {
      {
        data = { ...data, filters: { ...data.filters, date_range: {} } }
      }
    }
    dispatch(getAllPracticalTrainings(data))
  }

  const handleDelete = () => {
    dispatch(deletePracticalTraining(itemIdToDelete))
      .then(action => {
        const { statusCode, success, message } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          setTrigger(!trigger)
          setShowDeleteModal()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const onPageChange = page => {
    setPayload({ page_number: page })
    setTrigger(!trigger)
  }

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  const handleTabFilter = item => {
    if (showAll) {
      return true
    } else if (item.name === "Passed") {
      if (permissions.includes("can_show_passed_tab")) {
        return true
      } else {
        return false
      }
    } else if (item.name === "Failed") {
      if (permissions.includes("can_show_failed_tab")) {
        return true
      } else {
        return false
      }
    } else if (item.name === "Not Invited") {
      if (permissions.includes("can_show_not_invited_tab")) {
        return true
      } else {
        return false
      }
    }
    return true
  }

  const toggleTab = tab => {
    if (tab !== activeTab) {
      dispatch(setActiveTab(tab))
      if (tab !== 0) {
        if (isShowFilter !== false) dispatch(toggleFilterVisibility())
      }
      handleReset()
    }
  }

  const handleExportXLSX = async () => {
    try {
      setIsExporting(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token

      let data = {
        export_type: "practical_participants_list",
        filters: {
          keyword: participantsListPayload.filters.keyword || "",
          type: activeTab,
          job_title_ids: participantsListPayload.filters.job_title_ids,
        },
      }

      const request =
        activeTab === 0
          ? "api/v1/admin/practical/export"
          : "api/v1/admin/export/by-type"

      if (activeTab === 0) {
        data = {
          filters: {
            keyword: payload.filters.keyword,
            date_range:
              payload.filters.date_range.length > 1
                ? {
                    start_date: formatDateForAPI(payload.filters.date_range[0]),
                    end_date: formatDateForAPI(payload.filters.date_range[0]),
                  }
                : {},
          },
        }
      }

      const response = await axios.post(`${API_URL}/${request}`, data, {
        headers: {
          Authorization: token, // Attach token in headers
        },
        responseType: "blob", // Important for file downloads
      })

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        activeTab === 0
          ? "Training Rooms.xlsx"
          : activeTab === 1
          ? "Enrolled Participants.xlsx"
          : activeTab === 2
          ? "Attended Participants.xlsx"
          : activeTab === 3
          ? "Passed Participants.xlsx"
          : activeTab === 4
          ? "Failed Participants.xlsx"
          : activeTab === 5
          ? "Absent Participants.xlsx"
          : "Not Invited Participants.xlsx"
      ) // Set filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url) // Clean up memory
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExporting(false)
    }
  }

  if (loading) return <Spinners />

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="12">
              <div className="d-flex justify-content-between mb-4 align-items-center">
                <h4 className="card-title ">{t("Practical Training")}</h4>
                <div className="d-flex gap-3">
                  {(showAll || permissions.includes("can_create")) && (
                    <button
                      className="main_btn"
                      onClick={() => {
                        navigate("/practical-training/create")
                      }}
                    >
                      {t("Add New")}
                    </button>
                  )}
                  <button
                    className="main_btn fit_content"
                    onClick={handleExportXLSX}
                    disabled={isExporting}
                  >
                    {t("Download XLSX")}
                  </button>
                  {activeTab === 0 && (
                    <Button
                      color="primary"
                      onClick={() => dispatch(toggleFilterVisibility())}
                      title={t("Show/Hide Filter")}
                    >
                      {isShowFilter ? <FilterX /> : <Filter />}
                    </Button>
                  )}
                </div>
              </div>

              <Nav tabs className="one_line_parent mb-3 mt-3">
                {navItems.filter(handleTabFilter).map(item => (
                  <NavItem key={item.id}>
                    <NavLink
                      className={activeTab === item.id ? "active" : ""}
                      onClick={() => toggleTab(item.id)}
                    >
                      {t(item.name)}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              {isShowFilter && (
                <Filters
                  filters={filters}
                  state={{ ...payload.filters, order_by: payload.order_by }}
                  setState={data => {
                    const { order_by, ...rest } = data
                    dispatch(
                      setPayload({
                        order_by: Number(order_by),
                        filters: { ...payload.filters, ...rest },
                      })
                    )
                  }}
                  handleSearch={() => setTrigger(!trigger)}
                  handleReset={handleReset}
                />
              )}
              {activeTab === 0 && (
                <TableComponent
                  data={practicalTrainings}
                  loading={loading}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  onPageChange={onPageChange}
                  itemIdToDelete={itemIdToDelete}
                  isLoading={isLoading}
                  onMarkAttendanceClick={item => {
                    setModalType("MARK_ATTENDANCE")
                    setSelectedUuid(item?.uuid)
                    setSelectedItem(item)
                    toggle()
                  }}
                  onDeleteClick={item => {
                    setItemIdToDelete(item.uuid)
                    setShowDeleteModal(true)
                  }}
                />
              )}
              {activeTab !== 0 && <CandidateTabs activeTab={activeTab} />}
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Practical Training"),
        })}
      />
      <MarkAttendanceModal
        isOpen={modal}
        toggle={toggle}
        modalType={modalType}
        getAllData={() => setTrigger(!trigger)}
        selectedUuid={selectedUuid}
        selectedItem={selectedItem}
      />
    </React.Fragment>
  )
}

export default PracticalTraining
